import { SerializedPlainDate } from './api/data.types';
import {
  ChargerPlugType,
  ChargerPowerType,
  ParkingArea,
  ParkingRentingPartyType,
  ParkingSpace,
} from './parking.types';
import { Resident } from './Resident';

export interface ContractOptions {
  value: string | number;
  label: string | number;
}

/** Contract instance encoded in JWT idToken cookie */
export type TokenContract = {
  contractId: string;
  apartmentId: string;
  realestateId: string;
  buildingId?: string;
  role?: ContractRole;
  stairId?: string;
};

export type WaterFeeType =
  | 'ADVANCE_AND_EQUALIZATION'
  | 'METER_IN_APARTMENT'
  | 'PER_PERSON_PER_MOUNTH'
  | 'INCLUDED_IN_RENT';

export type ParkingSpaceType =
  | 'UNCOVERED'
  | 'SHED'
  | 'GARAGE'
  | 'CARPORT'
  | 'COVER';

export type ParkingSpaceContract = {
  /** ID of this parking contract */
  parkingContractId: string;
  parkingContractType: ContractType;
  /** ID of the rented parking space */
  parkingSpaceId: string;
  /**
   * ID of the rented parking product. Might not be always defined if contract only points to
   * a parking space, but not a product.
   */
  parkingProductId?: string;
  contractStatus?: ContractStatusCode;
  contractEndDate: SerializedPlainDate | null;
  /** Number of the parking space, e.g. "021", "003" */
  parkingSpaceNumber: string;
  contractStartingDate: SerializedPlainDate | null;
  parkingSpacetype: ParkingSpaceType | null;
  /** True if this parking space is a "Kiintiöpaikka" or "numberoimaton kiintiöpaikka" */
  isQuotaPlace: boolean;
  /** Monthly rent of this parking contract, in Euros */
  grossTotal: number;
  realEstateId: string;
  address?: string;
  /** List of names in format `"firstName lastName"` */
  contractSigners?: string[];
  /** Whether this contract was rented through OmaSATO web form */
  isQuickRent: boolean;
  /** Code to access the parking area */
  parkingAreaCode?: string;
  /** Contact IDs of persons who are contract parties in this contract */
  renterContactIds?: string[];
  /**
   * Contracts have a field "Lisätietoja irtisanomisesta" that customer support uses to
   * describe an unusual termination case. This could be, for example, an exception
   * to the termination date that was discussed with the customer that support has to
   * manually do.
   *
   * User should not be able to switch parking spaces if this is true.
   */
  hasTerminationInformationText: boolean;
  rentingParty: ParkingRentingPartyType;

  parkingSpace: ParkingSpace;
  parkingArea: ParkingArea;
};

export type ContractDepositType =
  | 'MONEY'
  | 'COMMITMENT'
  | 'DEPOSIT'
  | 'NO_DEPOSIT';

export type ContractDepositStatus =
  | 'OPEN'
  | 'PAID'
  | 'COMPENSATED'
  | 'EXEMPT'
  | 'NO_DEPOSIT'
  | 'RECEIVED'
  | 'IN_PROGRESS'
  | 'COMPLETED';

export interface ContractResponse {
  /**
   * First day the user may terminate without a penalty fee ("sopimussakko"). Always the
   * first day of a month.
   *
   * Note: the actual fee-free cutoff is one month later than this date, since termination
   * is only possible at month-end and a full month must pass first — e.g. `2026-10-01`
   * means the earliest fee-free end date is `2026-11-30`, not `2026-10-31`.
   *
   * Only for `INDEFINITELY_WITH_END_DATE_TYPE` contracts (pre-AHVL-reform, signed before
   * 1.10.2026). Superseded by `earliestPossibleEndDate` for newer contracts.
   */
  firstTerminationDate?: SerializedPlainDate | null;

  /**
   * First day the user may pick to terminate without a penalty fee ("sopimussakko").
   * Always the last day of the month.
   *
   * Only for `INDEFINITELY_AND_FIXED_PERIOD_TYPE` contracts. Marks the end of the contract's
   * fixed period before it converts to indefinite.
   *
   * @link https://satocorporation.atlassian.net/browse/JKB-9528
   */
  earliestPossibleEndDate?: SerializedPlainDate | null;

  address?: string;
  contractId?: string;
  contractSigners?: string[];
  dateOfStartingRent?: string;
  deposit: {
    depositAmount: number | null;
    depositType: ContractDepositType;
    depositStatus: ContractDepositStatus;
  };
  dueDate?: number;
  /** `true` if resident has been evicted */
  eviction?: boolean | null;
  signatureDate?: string | number;
  startDate: string;
  statusCodeOfContract?: ContractStatusCode;
  water?: string | number | boolean;
  waterFeeType: WaterFeeType;
  type: ContractType;
  rent?: number;
  invoice?: ContractInvoiceDetails;
  contractLines?: ContractPaymentLine[];
  /** Total sum of contract lines */
  monthlyTarget: number;
  /**
   * Rent review month as integer from 1 to 12, (1=jan, 12=dec). The month when rent is reviewed for adjustments (usually raised).
   * For non-ARA apartments this is the month when the first rent was paid; hence the name. For ARA apartments it's always in February.
   */
  firstPaymentPeriod: number;
  parkingSpaces?: ParkingSpaceContract[];
  // TODO: WIP implementation, currently not returning a value from CRM
  residentCount: number;
  /**
   * The contract id for the contract that this contract will be replaced by / is replaced.
   *
   * Sometimes there is a need to replace a contract with a new one, even if the resident stays in the same apartment.
   */
  replacingContractId?: string;
  /** The contract id that this contract replaced / will be replaced with. */
  originalContractId?: string;
  parkingSpaceOfParkingContract?: ParkingSpaceOfParkingContract;
}
export interface ParkingSpaceOfParkingContract {
  chargerPlugType: ChargerPlugType | null;
  chargerPower: ChargerPowerType | null;
  electricCarCharger: boolean;
  heatPole: boolean;
  labelId: string;
  motorcycleSpace: boolean;
  name: string;
  price: number;
  type: ParkingSpaceType;
  parkingSpaceId: string;
  parkingProductId: string;

  /** Code for accessing the physical parking area space */
  parkingAreaCode: string;

  parkingSpace: ParkingSpace;
  parkingArea: ParkingArea;
}
export interface Contract extends ContractResponse {
  residents: Resident[];
}

export interface ContractPaymentLine {
  total: number;
  type: PaymentType;
  name: string;
  grossTotal: number;
  alv: string;
  startDate: string;
  endDate?: string | null;
  // TODO: maybe not the best API schema for this purpose
  unit: 'EUR' | 'kk';
  quantity: number;
}

export type PaymentType =
  | 'SAUNA_FEE'
  | 'WATER_FEE'
  | 'RENT'
  | 'PARKING_FEE'
  | 'RENT_REFUND'
  | 'WAREHOUSE_RENT'
  | 'EXTRA_RENT'
  | 'RENT_DISCOUNT';

/*
  iban is used for Nordea IBAN information
  iban2 is used for OP IBAN information
*/
export interface ContractInvoiceDetails {
  company: { name: string; iban: string; iban2: string };
  referenceNumber: string;
}

export type ContractRole = 'PARTY' | 'RESIDENT';

export type ContractType =
  // Indefinite (toistaiseksi voimassa oleva), no minimum commitment period.
  | 'INDEFINITELY_TYPE'
  // Indefinite with a penalty-fee-bound minimum period, see `firstTerminationDate`.
  | 'INDEFINITELY_WITH_END_DATE_TYPE'
  // Indefinite with a fixed initial period, see `earliestPossibleEndDate`.
  | 'INDEFINITELY_AND_FIXED_PERIOD_TYPE'
  // Fixed-term (määräaikainen), ends outright on its end date.
  | 'FIXED_PERIOD_TYPE';

export enum ContractStatusCode {
  ACTIVE_STATUS = 'ACTIVE_STATUS',
  RESIGNED_STATUS = 'RESIGNED_STATUS',
  ENDED_STATUS = 'ENDED_STATUS',
  ENDED_A_STATUS = 'ENDED_A_STATUS', // Short for "Ended active". User is allowed to use a restricted version of the site if the contract has ended, but remains active.
  STARTING_STATUS = 'STARTING_STATUS',
  TERMINATION_PENDING = 'TERMINATION_PENDING', // Backend checks the status of the service request (palvelupyyntö) and returns this status when termination process is ongoing.
}

export type ContractHistoryInstance = {
  id: string;
  type: ContractType;
  target: 'APARTMENT' | 'PARKING_SPACE' | 'BUSINESS_SPACE';
  name: string;
  address: string;
  startDate: SerializedPlainDate;
  endDate: SerializedPlainDate;
};
